import React from 'react';

import { cn } from '@/lib/utils';
import { resolveStatus, statusColorStyle, toneClasses, type StatusKind } from '@/lib/status';

export interface StatusBadgeProps {
    status: string | null | undefined;
    kind: StatusKind;
    /** API-supplied colour (lead statuses) wins over the kind mapping. */
    color?: string | null;
    /** Override the derived label. */
    label?: string;
    className?: string;
}

/**
 * Standard CRM status chip. Resolves `status` + `kind` to a tone and label via
 * `resolveStatus`, and renders semantic-token classes via `toneClasses` — or,
 * when an API-supplied `color` is present (e.g. tenant-defined lead statuses),
 * an inline style from `statusColorStyle` instead.
 *
 * See docs/CRM_UI_CONVENTIONS.md section 4 — use this instead of a local
 * `getStatusColor`/`getStatusBadge` map.
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
    status,
    kind,
    color,
    label,
    className,
}) => {
    if (!status && !label) {
        return null;
    }

    const resolved = resolveStatus(status, kind);
    const displayLabel = label ?? resolved.label;
    const hasColor = Boolean(color && color.trim());

    return (
        <span
            className={cn(
                'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
                !hasColor && toneClasses(resolved.tone),
                className
            )}
            style={hasColor ? statusColorStyle(color) : undefined}
        >
            {displayLabel}
        </span>
    );
};
